import {InteractionType} from "@azure/msal-browser";
import {Client} from "@microsoft/microsoft-graph-client";
import {AuthCodeMSALBrowserAuthenticationProvider} from "@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser";

import {Errors, KAError} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

import {
    CANCELLED_LOGIN_MESSAGE,
    INTERACTION_IN_PROGRESS_MESSAGE,
    NO_CONSENT_MESSAGE,
} from "./constants.ts";
import {createPublicClientApplication} from "./public-client-application.ts";
import {
    isAccessDeniedError,
    isInteractionInProgressError,
    isUserCancelledError,
} from "./types.ts";

const MS_SCOPES = ["User.Read", "Files.ReadWrite"];

/**
 * Get an initialized Microsoft Graph API client.
 *
 * @returns A promise for an initialized Microsoft Graph API client.
 */
export async function getGraphClient(): Promise<Client> {
    const pca = createPublicClientApplication();
    await pca.initialize();

    let account = pca.getActiveAccount();

    if (!account) {
        // Authenticate via popup to get the user's account
        const authResult = await pca
            .acquireTokenPopup({
                scopes: MS_SCOPES,
                extraQueryParameters: {
                    ui_locales: RequestInfo.kaLocale,
                },
            })
            .catch((error) => {
                if (isInteractionInProgressError(error)) {
                    throw new KAError(
                        INTERACTION_IN_PROGRESS_MESSAGE,
                        Errors.Service,
                        {cause: error.message},
                    );
                }
                if (isUserCancelledError(error)) {
                    throw new KAError(
                        CANCELLED_LOGIN_MESSAGE,
                        Errors.Unauthorized,
                        {cause: error.message},
                    );
                }
                if (isAccessDeniedError(error)) {
                    throw new KAError(NO_CONSENT_MESSAGE, Errors.Unauthorized, {
                        cause: error.message,
                    });
                }
                throw new KAError("Could not authenticate", Errors.Service, {
                    cause: error,
                });
            });
        pca.setActiveAccount(authResult.account);
        account = authResult.account;
    }

    const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(pca, {
        account,
        interactionType: InteractionType.Silent, // avoid a second popup opening and immediately closing
        scopes: MS_SCOPES,
    });

    const graphClient = Client.initWithMiddleware({authProvider: authProvider});
    return graphClient;
}
